<script setup>
import { onMounted, ref, nextTick, computed } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { CoursesClient } from "@/services/CoursesClient";
import { ChaptersClient } from "@/services/ChaptersClient";
import ChapterPage from "@/views/student/components/ChapterPage.vue";
import "photoswipe/style.css";
import Billing from "@/views/student/components/Billing.vue";
import RatingTable from "@/views/student/components/RatingTable.vue";
import NotesModal from "@/views/student/components/Notes.vue";
import Swal from "sweetalert2";
import CertificateGenerator from "@/views/student/components/CertificateGenerator.vue";
import { RoadmapClient } from "@/services/RoadmapClient";

const showCertificate = ref(false);
const store = useStore();
const route = useRoute();
const coursesClient = new CoursesClient();
const chaptersClient = new ChaptersClient();

const selectedChapterId = ref(null);
const chapters = ref([]);

// Course data state
const course = ref(null);
const loading = ref(true);
const error = ref(null);

// Other existing state
const isQuiz = ref(false);
const showReviewModal = ref(false);
const userNotes = ref("");
const notesModalRef = ref(null);
const showBillingModal = ref(false);
const courseImage = ref(null);
const courseId = route.params.curso;

const roadmapClient = new RoadmapClient();
const roadmap = ref([]);

const isAuthenticated = computed(() => store.state.isAuthenticated); // Now using Vuex state

const fetchAllCourseData = async () => {
  try {
    loading.value = true;
    const courseId = route.params.curso;

    // Fetch all in parallel
    const [courseResponse, chaptersResponse, roadmapResponse] = await Promise.all([
      coursesClient.getCourseById(courseId),
      chaptersClient.getAllChaptersByCourse(courseId),
      roadmapClient.getRoadmap(courseId)
    ]);

    // Handle course
    course.value = courseResponse;

    // Handle image
    if (courseResponse.portada?.url) {
      try {
        courseImage.value = await getImageUrl(courseResponse.portada.url);
      } catch {
        courseImage.value = require('@/assets/img/home-decor-1.jpg');
      }
    } else {
      courseImage.value = require('@/assets/img/home-decor-1.jpg');
    }

    // Merge chapters + roadmap
    chapters.value = chaptersResponse.map(chap => {
      const roadmapChap = roadmapResponse.find(r => r.capitulo_id === chap.id);
      return {
        ...chap,
        isLocked: roadmapChap?.locked ?? false,
        isCompleted: roadmapChap?.completed ?? false,
      };
    });

    roadmap.value = roadmapResponse;

    // Select first chapter that is not completed
    const firstIncomplete = chapters.value.find(chap => !chap.isCompleted);
    selectedChapterId.value = firstIncomplete ? firstIncomplete.id : (chapters.value[0]?.id ?? null);

  } catch (err) {
    error.value = "Error al cargar el curso";
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const getImageUrl = async (s3Key) => {
  const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${s3Key}`;
  const response = await fetch(url, {
    headers: {
      Accept: 'text/plain',
      Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
    }
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return await response.text();
};

// Existing methods (keep them as is)
const navbarMinimize = () => store.commit("navbarMinimize");
const openNotesModal = () => notesModalRef.value.show();
const saveNotes = (notes) => { userNotes.value = notes; };

const goToChapter = (chapterId) => {
  // Access the value of the reactive reference
  const chapter = chapters.value.find(c => c.id === chapterId);

  // Check if chapter exists
  if (!chapter) {
    console.error('Chapter not found:', chapterId);
    return;
  }

  // Check if not enrolled and chapter isn't free
  if (!course.value.matriculado && !chapter.es_gratuito) {
    Swal.fire({
      title: 'Acceso restringido',
      text: 'Debes matricularte en este curso para acceder a este capítulo',
      icon: 'warning',
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#3085d6',
    });
    return; // Prevent navigation
  } else if(chapter.isLocked){
    Swal.fire({
      title: 'Acceso restringido',
      text: 'Debes completar contenido previo para acceder a este capítulo',
      icon: 'warning',
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#3085d6',
    });
    return; // Prevent navigation
  }  

  selectedChapterId.value = chapterId;
  isQuiz.value = false;

  // Scroll to chapter section after the next DOM update
  nextTick(() => {
    const chapterSection = document.querySelector('.mt-0.mt-lg-5.row');
    if (chapterSection) {
      chapterSection.scrollIntoView({ behavior: 'smooth' });
    }
  });
};

const openBillingModal = () => { showBillingModal.value = true; };
const closeBillingModal = () => { showBillingModal.value = false; };

// Lifecycle hooks
onMounted(() => {
  navbarMinimize();
  fetchAllCourseData();
});

onMounted(() => {

  if (!isAuthenticated.value) {
    store.state.showSidenav = false;
  } else {
    store.state.showSidenav = true;
  }

});


const formatDate = (dateString) => {
  if (!dateString) return 'Fecha no disponible';
  const options = { year: 'numeric', month: 'numeric', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('es-ES', options);
};
</script>

<template>
  <div class="p-2 p-lg-4 container-fluid">
    <div v-if="loading" class="text-center py-10 my-10">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else-if="course" class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-xl-7 col-lg-3" ref="chapterSection">
          <div class="position-relative mb-3">
          <!-- Course Image -->
          <img
            class="mx-auto shadow-lg img-responsive border-radius-lg w-100"
            :src="courseImage"
            :alt="course.titulo"
            style="height: 450px; object-fit: cover;"
          />

          <!-- Top-right category badge -->
          <span class="badge bg-primary position-absolute top-2 end-2">
            {{ course.categoria_nombre }}
          </span>

          <!-- Bottom-left professor badge -->
          <span class="badge bg-secondary position-absolute bottom-2 start-2">
            <i class="fas fa-user-tie me-1"></i> {{ course.profesor_nombre }}
          </span>
        </div>               
            <hr class="horizontal dark mt-0 mb-0 mt-lg-3" />

            <!--Seccion de Capitulo-->
            <div class="mt-0 mt-lg-3 row" v-if="selectedChapterId">
              <div class="mx-auto col-lg-12">
                <chapter-page :chapter-id="selectedChapterId" :course-id="route.params.curso" />
              </div>
            </div>
          </div>

          <div class="mx-auto col-lg-5 ">
            <div class="position-sticky top-10">
      

            <h4 class="mt-4 mt-lg-0">{{ course.titulo }}</h4>                
            <div class="row mt-2">
              <div class="d-flex justify-content-between">
                <p class="mb-3 mb-lg-3 text-xs">
                  <i class="fas fa-book-open pe-1"></i>
                  {{ chapters.length }} Capítulos
                </p>
                 <p class="mb-3 mb-lg-3 text-xs">
                  Creado el {{ formatDate(course.fecha_creacion) }}
                 </p>
              </div>
            </div>

            <p class="mb-6 text-sm" v-html="course.descripcion || 'No hay descripción disponible'"
              style="max-height: 300px; overflow-y: auto;"></p>
            <hr class="horizontal dark mt-0 mb-0 mt-lg-3" />

            <div class="timeline timeline-one-side ms-3">
              <div v-for="chapter in chapters" :key="chapter.id" class="timeline-block mb-1 mb-lg-2 ps-3">
                <div class="timeline-link" :class="selectedChapterId === chapter.id ? 'bg-light' : '' " @click="goToChapter(chapter.id)">
                  <span
                    class="timeline-step"
                    :class="selectedChapterId === chapter.id ? 'bg-dark' : 'bg-secondary'"
                  >
                    <i
                      :class="`fas ${
                        (!chapter.isLocked && (chapter.es_gratuito || course.matriculado)) ? 'fa-play' : 'fa-lock'
                      } fa-sm text-white`"
                    ></i>
                  </span>
                  <div class="timeline-content w-100">
                    <div class="d-flex align-items-center justify-content-between w-100">
                      <h6 class="text-dark text-sm font-weight-bold mb-0">
                        {{ chapter.titulo }}                      
                      </h6>                    
                      <i
                        v-if="chapter.isCompleted"
                        class="fas fa-check-circle text-primary ms-2"
                        title="Completado"
                      ></i>                     
                    </div>
                    
                    <p class="text-secondary font-weight-bold text-xs mt-1 mb-0">
                      Capítulo {{ chapter.orden }}
                      <span
                        v-if="chapter.es_gratuito && !course.matriculado"
                        class="badge badge-sm badge-info ms-2"
                      >
                        Gratis
                      </span>
                      <span
                        v-if="!chapter.es_gratuito && !course.matriculado"
                        class="badge badge-sm badge-info ms-2"
                      >
                        <i                         
                        class="fas fa-star fa-xxs text-xxs text-primary"
                      ></i>
                      </span>                     
                    </p>
                  </div>
                  
                </div>
              </div>

            </div>

            <hr v-if="!course.matriculado" class="horizontal dark mt-4 mb-3" />

            <div v-if="!course.matriculado" class="mt-4 mb-4 row">
              <div class="col-lg-6 d-flex align-items-center justify-content-between text-center text-lg-end">
                <h6 class="mb-3 mb-md-0">Matrícula por: </h6>
                <h5 class="mb-0 text-info">
                  {{ course.moneda === 0 ? '₡' : '$' }}
                  {{ course.precio || '0.00' }}
                </h5>
              </div>
              <div class="col-lg-6">
                <div v-if="!isAuthenticated" class="text-center text-muted bg-gray-100 rounded p-2 mb-2 small">
                  <i class="fas fa-info-circle pe-2"></i>Debes <router-link to="/autenticacion/iniciar-sesion" class="fw-bold text-info text-decoration-none">Iniciar Sesión</router-link>
                  o <router-link to="/autenticacion/registro" class="fw-bold text-info text-decoration-none">Registrarte</router-link>
                  para matricularte en este curso.
                </div>
                <button v-if="isAuthenticated" class="mb-0 btn bg-gradient-info mt-lg-auto w-100" type="button" @click="openBillingModal">
                  <i class="fas fa-star pe-2"></i> Matricular
                </button>
              </div>


            </div>
            <!-- v-if="courseCompleted"-->
            <div v-else class="mt-4 mb-4 row">
              <div class="col-lg-12" v-if="course.porcentaje_completado >= 100">
                <button class="btn btn-warning w-100" @click="showCertificate = true"><i
                    class="fas fa-certificate me-2"></i>Obtener Certificado</button>

              </div>
              <div v-else class="progress-wrapper ">
              <div class="progress-info">
                <div class="progress-percentage">
                  <span class="text-sm font-weight-bold">Progreso: {{ course.porcentaje_completado }}%</span>
                </div>
              </div>
              <div class="progress">
                <div class="progress-bar bg-success" role="progressbar"
                  :style="{ width: course.porcentaje_completado + '%' }" :aria-valuenow="course.porcentaje_completado"
                  aria-valuemin="0" aria-valuemax="100">
                </div>
              </div>
            </div>
            </div>
           
          </div>

          </div>
        </div>
        <!-- v-if="courseCompleted"
        <hr class="horizontal dark mt-0 mb-0 mt-lg-5" />

        <div class="mt-5 row">
          <div class="mx-auto col-lg-12">
            <rating-table @openReviewModal="showReviewModal = true" />
          </div>
        </div>-->
      </div>
    </div>

    <button v-if="isAuthenticated" class="notes-floating-btn" @click="openNotesModal">
      <i class="fas fa-sticky-note btn-icon"></i>
      <span class="btn-text">Mis Notas</span>
    </button>

    <notes-modal ref="notesModalRef" :initial-notes="userNotes" :course-id="courseId" @saveNotes="saveNotes" />
    <billing v-if="showBillingModal" @close="closeBillingModal" :course-name="course.titulo" :amount="course.precio"
      :currency="course.moneda" :course-id="course.id" :courseImage="courseImage"/> 
    <certificate-generator
      :show="showCertificate" @close="showCertificate = false" />
  </div>
</template>

<!-- Keep your existing styles -->
<style>
/* Your existing styles remain the same */
.timeline-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 0px 5px 5px 0px;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.timeline-link {
  cursor: pointer;
  text-decoration: none;
  display: flex;
  align-items: center;
}

.timeline-link:hover {
  background-color: #ebf1fc !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.timeline-link:hover .text-dark {
  text-decoration: underline;
}

.file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
}

.position-fixed {
  position: fixed;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.25em;
}

@media (max-width: 768px) {
  .img-responsive {
    max-height: 250px !important;
    min-height: auto !important;
  }
}

.notes-floating-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 990;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2152ff;
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.16);
  transition: all 0.3s ease;
  overflow: hidden;
}

.notes-floating-btn:hover {
  border-radius: 10px;
  width: 145px;
  justify-content: flex-start;
  padding-left: 20px;
}

.btn-icon {
  font-size: 1.25rem;
  position: absolute;
  transition: all 0.3s ease;
}

.btn-text {
  opacity: 0;
  width: 0;
  white-space: nowrap;
  transition:
    opacity 0.2s ease 0.1s,
    width 0.3s ease;
}

.notes-floating-btn:hover .btn-icon {
  position: relative;
  margin-right: 8px;
}

.notes-floating-btn:hover .btn-text {
  opacity: 1;
  width: auto;
}
</style>